/**
 * Aplicação da identidade visual do cliente.
 *
 * O cliente escolhe UMA cor. Tudo que depende dela — inclusive a cor do texto
 * que vai por cima — é derivado aqui, porque quem escolhe um amarelo claro no
 * painel de operação não está pensando em contraste, e texto branco sobre
 * amarelo é ilegível.
 */

/** Luminância relativa (WCAG 2.1, 1.4.3). */
function luminancia(hex: string): number {
  const c = hex.replace("#", "");
  const canal = (i: number) => {
    const v = parseInt(c.slice(i * 2, i * 2 + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(0) + 0.7152 * canal(1) + 0.0722 * canal(2);
}

const contraste = (a: number, b: number) =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);

/** Mistura em direção ao preto — usada quando a cor é clara demais para texto. */
function escurecer(hex: string, fator: number): string {
  const c = hex.replace("#", "");
  const p = (i: number) =>
    Math.round(parseInt(c.slice(i * 2, i * 2 + 2), 16) * (1 - fator))
      .toString(16).padStart(2, "0");
  return `#${p(0)}${p(1)}${p(2)}`;
}

export const CORES_SUGERIDAS = [
  "#1c6ba8", "#0f766e", "#4338ca", "#b45309", "#b91c1c", "#334155",
];

export const corValida = (cor: string) => /^#[0-9a-fA-F]{6}$/.test(cor.trim());

/**
 * Aplica a fonte escolhida.
 *
 * A folha do Google Fonts entra uma vez por família e fica: trocar de fonte na
 * prévia não pode recarregar a página nem piscar o texto. `display=swap` para o
 * texto aparecer na fonte do sistema enquanto a escolhida baixa — painel que
 * some o texto por 200 ms parece quebrado.
 */
const fontesCarregadas = new Set<string>();

export function aplicarFonte(fonte: string | null | undefined,
                             raiz = document.documentElement) {
  if (!fonte) {
    raiz.style.removeProperty("--font-sans");
    return;
  }
  if (!fontesCarregadas.has(fonte)) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${
      encodeURIComponent(fonte).replace(/%20/g, "+")}:wght@300;400;500;600;700&display=swap`;
    document.head.appendChild(link);
    fontesCarregadas.add(fonte);
  }
  raiz.style.setProperty("--font-sans", `"${fonte}", ui-sans-serif, system-ui, sans-serif`);
}

/**
 * Define os tokens da marca no documento. Devolve o que foi aplicado, para o
 * painel de operação poder mostrar a mesma prévia que o cliente vai ver.
 */
export function aplicarMarca(cor: string | null | undefined, raiz = document.documentElement) {
  if (!cor || !corValida(cor)) {
    raiz.style.removeProperty("--color-brand-tenant");
    raiz.style.removeProperty("--color-on-brand");
    raiz.style.removeProperty("--color-brand-text");
    return null;
  }
  const lum = luminancia(cor);
  // Texto por cima da cor: o que tiver mais contraste, não o que for mais bonito.
  const sobre = contraste(lum, 1) >= contraste(lum, 0) ? "#ffffff" : "#111110";

  // A mesma cor usada como TEXTO sobre fundo claro precisa de 4,5:1. Um azul
  // claro que funciona como preenchimento vira texto ilegível; escurece até passar.
  let texto = cor;
  for (let i = 0; i < 6 && contraste(luminancia(texto), 1) < 4.5; i++)
    texto = escurecer(cor, 0.12 * (i + 1));

  raiz.style.setProperty("--color-brand-tenant", cor);
  raiz.style.setProperty("--color-on-brand", sobre);
  raiz.style.setProperty("--color-brand-text", texto);
  return { cor, sobre, texto };
}
